import {useEffect, memo, useRef, useState} from 'react';

import {Grid} from '@mui/material';
import Box from '@mui/material/Box';
import {useWindowSize} from '@uidotdev/usehooks';
import {useLocation} from 'react-router-dom';

import {REPORT_MODULES_CATEGORIES, SIDEBAR_CONTACT_BUTTON_SIZE, SIDEBAR_MORE_ICON_SIZE} from '../../../const';
import {useIsModuleVisibleOrEnabled} from '../../../hooks/components/report-modules/useIsModuleVisibleOrEnabled/useIsModuleVisibleOrEnabled';
import useMainSidebar from '../../../hooks/providers/useMainSidebar';
import useReport from '../../../hooks/providers/useReport';
import {calculateNeededSpaceForSidebarCards, calculateNumberOfSidebarItemsDisplayable} from '../../../utils';
import ChooseViewPopper from '../../embed/report-views/ChooseViewPopper/ChooseViewPopper';
import OpenMoreReportsOrModulesPopperButton from '../OpenMoreReportsOrModulesButton/OpenMoreReportsOrModulesButton';
import ReportModulesCategory from '../ReportModulesCategory/ReportModulesCategory';

// eslint-disable-next-line react/prop-types
const ReportModulesList = () => {
  const location = useLocation();
  const windowSize = useWindowSize();
  const {
    shouldDisplayContactButton,
    setModuleCategoryExpanded: setCategoryExpanded,
    setNotVisibleSidebarModules,
    reportModulesContainerHeight: containerHeight,
    setReportModulesContainerHeight: setContainerHeight,
    modulesListContainerRef: containerRef
  } = useMainSidebar();
  const {selectedProject} = useReport();

  const {isModuleVisible, getNumberOfVisibleModulesCategories} = useIsModuleVisibleOrEnabled();
  const totalNumberOfModulesCategoriesToDisplay = getNumberOfVisibleModulesCategories();

  const [canDisplayAllModules, setCanDisplayAllModules] = useState(false);
  const [numberOfModulesCategoriesDisplayable, setNumberOfModulesCategoriesDisplayable] = useState(0);

  useEffect(() => {
    const contactButtonHeight = containerRef?.current?.clientHeight > 90 ? SIDEBAR_CONTACT_BUTTON_SIZE : 0;

    if (containerRef.current) {
      setContainerHeight(containerRef.current.clientHeight - contactButtonHeight);
    }
    const resizeObserver = new ResizeObserver(() => {
      if (containerRef.current) {
        setContainerHeight(containerRef.current.clientHeight - contactButtonHeight);
      }
    });
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [shouldDisplayContactButton]);

  useEffect(() => {
    if (containerRef?.current) {
      setContainerHeight(containerRef.current.clientHeight);
    }
  }, [windowSize.height]);

  useEffect(() => {
    const canDisplayAll = calculateNeededSpaceForSidebarCards(totalNumberOfModulesCategoriesToDisplay) < containerHeight;
    const containerHeightWithoutMoreIcon = canDisplayAll ? containerHeight : containerHeight - SIDEBAR_MORE_ICON_SIZE;

    const numberModulesDisplayable = calculateNumberOfSidebarItemsDisplayable(containerHeightWithoutMoreIcon);

    setCanDisplayAllModules(canDisplayAll);
    setNumberOfModulesCategoriesDisplayable(numberModulesDisplayable);
  }, [selectedProject, containerHeight, windowSize.height]);

  const isReportPage = location.pathname.startsWith('/reports');
  const allVisibleCategories = Object.values(REPORT_MODULES_CATEGORIES).filter(category => {
    const categoryHasVisibleModules = category.modules.some(m => isModuleVisible(m));
    return categoryHasVisibleModules;
  });

  useEffect(() => {
    const notDisplayableModulesCategories = allVisibleCategories.slice(numberOfModulesCategoriesDisplayable);
    setNotVisibleSidebarModules(notDisplayableModulesCategories);
  }, [numberOfModulesCategoriesDisplayable, containerHeight, windowSize.height]);

  useEffect(() => {
    if (!isReportPage) {
      setCategoryExpanded(null);
    }
  }, [location.pathname]);

  if (!isReportPage) return null;

  const displayableModuleCategories = allVisibleCategories.slice(0, numberOfModulesCategoriesDisplayable);

  return (
    <Box ref={containerRef} display="flex" flex="auto" flexDirection="column" justifyContent="space-around" pt={1}>
      <Grid minHeight={150} alignItems="center" container flexDirection="column" flex={1} sx={{padding: '0 !important'}}>
        <Box>
          {displayableModuleCategories.map(c => {
            return (
              <Box mt={0.5} key={c.id}>
                <ReportModulesCategory isNestedInPopper={false} key={c.id} category={c} />
              </Box>
            );
          })}
        </Box>

        <OpenMoreReportsOrModulesPopperButton canDisplayAllItems={canDisplayAllModules} isMoreReportsButton={false} />
      </Grid>
      <ChooseViewPopper />
    </Box>
  );
};

export default memo(ReportModulesList);
